import { getOpxHostApp, MAC_OUTLOOK, OUTLOOK_DESKTOP } from 'owa-config';
import type { IStyle } from '@fluentui/style-utilities';
import React from 'react';

import classNames from 'classnames';

/** Object containing per-client stylesheets for a given component */
interface ClientStyles {
    /** Base stylesheet (OWA) */
    base?: string | {};
    /** Desktop client stylesheet */
    desktop?: string | {};
    /** Mac client stylesheet */
    mac?: string | {};
}

/**
 * Returns a memoized callback getClass for dynamically generating client-aware styling,
 * by combining classes matching the provided `className` from client stylesheets
 * that are relevant to the current user context. Use this for opx host specific class names.
 *
 * Returns a memoized callback getStyle for dynamically generating client-aware styling,
 * by combining styles matching the provided `className` from client stylesheets
 * that are relevant to the current user context. Use this for opx host specific styles object.
 */
export function useClientStyles(styles: ClientStyles) {
    const stylesRef = React.useRef(styles);

    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7051 (32,22): Parameter has a name but no type. Did you mean 'arg0: string'? ...
    // @ts-expect-error
    const getClass: (string) => string | undefined = React.useCallback((className: string) => {
        const { base = {}, desktop = {}, mac = {} } = stylesRef.current;
        switch (getOpxHostApp()) {
            case OUTLOOK_DESKTOP:
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS7053 (39,35): Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
                // @ts-expect-error
                return classNames(base[className], desktop?.[className]);
            case MAC_OUTLOOK:
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS7053 (44,35): Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
                // @ts-expect-error
                return classNames(base[className], mac?.[className]);
            default:
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS7053 (49,24): Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
                // @ts-expect-error
                return base[className];
        }
    }, []);

    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7051 (56,22): Parameter has a name but no type. Did you mean 'arg0: string'? ...
    // @ts-expect-error
    const getStyle: (string) => IStyle | undefined = React.useCallback((className: string) => {
        const { base = {}, desktop = {}, mac = {} } = stylesRef.current;
        switch (getOpxHostApp()) {
            case OUTLOOK_DESKTOP:
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS7053 (63,29): Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
                // @ts-expect-error
                return { ...base[className], ...desktop[className] };
            case MAC_OUTLOOK:
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS7053 (68,29): Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
                // @ts-expect-error
                return { ...base[className], ...mac[className] };
            default:
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS7053 (73,24): Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
                // @ts-expect-error
                return base[className];
        }
    }, []);

    return { getClass, getStyle };
}
